import { matchIntent, extractParams, filterBannedWords, scrubPII } from '../lib/spinoza/intents';

const ENDPOINT = '/api/v1/spinoza/chat';
const DISCLAIMER = 'I am not a financial adviser. This information is for educational purposes only.';

/**
 * SpinozaService — offline rule-based intent matching + optional online proxy.
 * PII scrubbed before any network call.
 */
class SpinozaService {
  constructor() {
    this.online = false;
  }

  /**
   * Process a user message.
   * @param {string} text User input
   * @param {Object} ctx Context: { selectStack, navigate, isAuthenticated }
   * @returns {Promise<{ text: string, intent: string|null }>}
   */
  async process(text, ctx = {}) {
    // PII scrub before processing
    const cleanText = scrubPII(text);

    // Check for recommendation requests
    const lower = cleanText.toLowerCase();
    if (
      lower.includes('recommend') ||
      lower.includes('should i') ||
      lower.includes('best investment') ||
      lower.includes('what should')
    ) {
      return {
        text: filterBannedWords(DISCLAIMER),
        intent: null,
      };
    }

    // Try online first
    if (this.online) {
      try {
        const response = await this._fetchOnline(cleanText, ctx);
        if (response) return response;
      } catch {
        // Fall through to offline
      }
    }

    // Offline: rule-based
    return this._processOffline(cleanText, ctx);
  }

  _processOffline(text, ctx) {
    const intent = matchIntent(text);
    const params = extractParams(text);

    if (intent) {
      const result = intent.run(ctx, params);
      return {
        text: filterBannedWords(result),
        intent: intent.name,
      };
    }

    // Default greeting / fallback
    const lower = text.toLowerCase();
    if (lower.includes('hello') || lower.includes('hi') || lower === 'hey') {
      return {
        text: 'Hello! I\'m Spinoza, your AI treasury assistant. I can help you navigate our seven services — yield discovery, exchange rates, compliance, hedging, liquidity, execution, and portfolio management. What would you like to explore?',
        intent: null,
      };
    }

    return {
      text: 'I can help you with yield discovery, exchange rates, compliance, hedging, liquidity, execution, or portfolio management. Try asking me to "show me rates for 10000 USD to KES" or "explain hedging".',
      intent: null,
    };
  }

  async _fetchOnline(text, ctx) {
    try {
      const token = typeof window !== 'undefined'
        ? localStorage.getItem('authToken')
        : null;

      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: scrubPII(text) }),
      });

      if (!res.ok) return null;

      const data = await res.json();
      return {
        text: filterBannedWords(data.reply || data.message || ''),
        intent: data.intent || null,
      };
    } catch {
      return null;
    }
  }

  /**
   * Get initial greeting message, contextual to the current page.
   * @param {string} pathname — current window.location.pathname
   */
  getGreeting(pathname = '/') {
    const p = pathname.toLowerCase();
    const intro = 'Hello! I\'m Spinoza, your AI treasury assistant.';

    if (p === '/pricing') return `${intro} Ask me anything about ForwardsFlow pricing.`;
    if (p === '/about') return `${intro} Ask me anything about ForwardsFlow and our mission.`;
    if (p === '/products') return `${intro} Ask me about our seven treasury services.`;
    if (p === '/contact') return `${intro} Need to reach the team? I can help point you in the right direction.`;
    if (p === '/apply' || p === '/open-account') return `${intro} I can walk you through the account opening process.`;
    if (p === '/demo') return `${intro} Interested in a demo? I can explain what to expect.`;
    if (p === '/returns-estimator') return `${intro} I can help you understand FX-adjusted frontier yields.`;
    if (p === '/simulator') return `${intro} Ask me anything about the investment simulator.`;
    if (p === '/privacy' || p === '/cookie-policy') return `${intro} Questions about our privacy practices? I\'m here to help.`;
    if (p === '/disclaimer' || p === '/terms') return `${intro} Questions about our terms? Ask away.`;
    if (p.startsWith('/investor/admin')) return `${intro} How can I help with your family office dashboard?`;
    if (p.startsWith('/investor')) return `${intro} How can I help with your portfolio today?`;
    if (p.startsWith('/bank/treasury')) return `${intro} How can I assist with treasury dealing?`;
    if (p.startsWith('/bank/forwards')) return `${intro} How can I assist with forwards dealing?`;
    if (p.startsWith('/bank/compliance')) return `${intro} How can I assist with compliance workflows?`;
    if (p.startsWith('/bank')) return `${intro} How can I assist with bank operations?`;
    if (p.startsWith('/admin')) return `${intro} How can I assist with platform administration?`;

    return `${intro} Ask me anything about cash flow management.`;
  }
}

const spinozaService = new SpinozaService();
export default spinozaService;
